/**
 * CloudNoiseGenerator — procedural 3D noise bake for volumetric clouds.
 *
 * Generates two volumetric textures:
 *
 *   1. **baseNoise** — 64³ RGBA8, layered value-noise FBM. This replaces
 *      the Schneider 128³ Perlin-Worley. At 64³ we trade some sharpness
 *      for 4× less memory (1 MB vs 4 MB after uncompressed RGBA), which
 *      ships cleanly without external `.ktx2` assets.
 *
 *      Channels:
 *        R — low-freq 3D FBM (large-scale shape)
 *        G — mid-freq 3D FBM (erosion scale)
 *        B — high-freq 3D FBM (fine detail within clouds)
 *        A — alternative low-freq (used for the "dilate" remap in density)
 *
 *   2. **detailNoise** — 32³ RGB8, high-frequency value-noise used only
 *      when inside a cloud to erode edges. ~96 KB.
 *
 *   3. **weatherMap** — 512×512 RGBA8, default procedural weather pattern:
 *      R = coverage, G = cloud-type (0.5 = stratocumulus), B = wetness,
 *      A = anvil-bias. Users can replace with custom authored weather.
 *
 * All generation is pure TypeScript, single-threaded — bake takes ~150 ms
 * on a modern laptop (acceptable one-time startup cost). If profiling
 * later shows this as a bottleneck, v2 can move it to a compute pass.
 */

import * as THREE from 'three/webgpu';
import { BakeCache } from '../../platform/BakeCache.js';

export interface CloudNoiseTextures {
  base: THREE.Data3DTexture;
  detail: THREE.Data3DTexture;
  weather: THREE.DataTexture;
}

/** The raw RGBA8 bytes of the three bakes (what the BakeCache persists). */
export interface CloudNoiseData {
  base: Uint8Array;     // 64³ × 4
  detail: Uint8Array;   // 32³ × 4
  weather: Uint8Array;  // 512² × 4
}

/** Bake parameters — part of the cache key so a retune never reads stale bytes. */
const BASE_SIZE = 64, BASE_FREQS = [2, 4, 8, 16], BASE_CHANNELS = 4;
const DETAIL_SIZE = 32, DETAIL_FREQS = [8, 16, 32, 32], DETAIL_CHANNELS = 3;
const WEATHER_SIZE = 512;
/** Bump whenever the noise/weather ALGORITHM changes (cached bytes are then ignored). */
export const CLOUD_NOISE_BAKE_VERSION = 1;
export const CLOUD_NOISE_CACHE_KEY =
  `cloud-noise:${BASE_SIZE}/${BASE_FREQS.join(',')}/${BASE_CHANNELS};${DETAIL_SIZE}/${DETAIL_FREQS.join(',')}/${DETAIL_CHANNELS};${WEATHER_SIZE}`;

/** The CPU bake alone — pure, deterministic, ~150–500 ms. */
export function bakeCloudNoiseData(): CloudNoiseData {
  return {
    base: _bake3DNoise(BASE_SIZE, BASE_FREQS, BASE_CHANNELS),
    detail: _bake3DNoise(DETAIL_SIZE, DETAIL_FREQS, DETAIL_CHANNELS),
    weather: _bakeDefaultWeatherMap(WEATHER_SIZE),
  };
}

/** Wrap baked (or cache-restored) bytes in textures with the canonical sampling parameters. */
export function cloudNoiseTexturesFromData(d: CloudNoiseData): CloudNoiseTextures {
  return {
    base: _volumeTexture(d.base, BASE_SIZE),
    detail: _volumeTexture(d.detail, DETAIL_SIZE),
    weather: _weatherTexture(d.weather, WEATHER_SIZE),
  };
}

/** True when the byte lengths match this generator's layout (a corrupt/foreign entry is rejected). */
export function isValidCloudNoiseData(d: Partial<CloudNoiseData> | null | undefined): d is CloudNoiseData {
  return !!d && d.base?.byteLength === BASE_SIZE ** 3 * 4 && d.detail?.byteLength === DETAIL_SIZE ** 3 * 4
    && d.weather?.byteLength === WEATHER_SIZE * WEATHER_SIZE * 4;
}

/**
 * Generate all three cloud textures synchronously (a fresh bake). Call once
 * at module startup; reuse the returned textures across all clouds.
 */
export function generateCloudNoise(): CloudNoiseTextures {
  return cloudNoiseTexturesFromData(bakeCloudNoiseData());
}

/** Start the cache read now (module construction) so a late init() finds the bytes waiting. */
export function prefetchCloudNoiseCache(cache: BakeCache = BakeCache.shared()): void {
  cache.prefetch(CLOUD_NOISE_CACHE_KEY, CLOUD_NOISE_BAKE_VERSION);
}

export interface CachedCloudNoise {
  textures: CloudNoiseTextures;
  /** 'cache' when the bytes were restored, 'bake' when generated (and stored). */
  source: 'cache' | 'bake';
}

/**
 * The same textures, restored from the BakeCache when a previous session
 * baked them (pixel-identical: same bytes, same texture parameters); otherwise
 * baked now and stored for next time. Never throws — any cache trouble
 * degrades to a plain bake.
 */
export async function generateCloudNoiseCached(cache: BakeCache = BakeCache.shared()): Promise<CachedCloudNoise> {
  const hit = await cache.get(CLOUD_NOISE_CACHE_KEY, CLOUD_NOISE_BAKE_VERSION);
  if (hit && hit.length === 3) {
    const d = { base: hit[0], detail: hit[1], weather: hit[2] };
    if (isValidCloudNoiseData(d)) return { textures: cloudNoiseTexturesFromData(d), source: 'cache' };
  }
  const d = bakeCloudNoiseData();
  void cache.put(CLOUD_NOISE_CACHE_KEY, CLOUD_NOISE_BAKE_VERSION, [d.base, d.detail, d.weather]);
  return { textures: cloudNoiseTexturesFromData(d), source: 'bake' };
}

// ─── Internals ──────────────────────────────────────────────────────────────

/**
 * Build a WxWxW Data3DTexture with `channels` layered FBM values.
 * Each channel samples FBM at a different base frequency from `freqs`.
 *
 * Output is RGBA8 (UnsignedByte) — 4 channels packed into a single volume
 * even when `channels` < 4 (unused channels filled with the first channel).
 */
function _bake3DNoise(
  size: number,
  freqs: number[],
  channels: number,
): Uint8Array {
  const pixelCount = size * size * size;
  const data = new Uint8Array(pixelCount * 4);

  // Build noise per channel, write into the interleaved RGBA8 buffer.
  for (let ch = 0; ch < 4; ch++) {
    const freq = freqs[Math.min(ch, channels - 1)];
    const noise = _fbm3D(size, freq);
    for (let i = 0; i < pixelCount; i++) {
      // Normalize to [0, 255]. FBM returns approximately [-1, 1] after
      // octave summation; remap by ½*x+½.
      const v = Math.max(0, Math.min(1, noise[i] * 0.5 + 0.5));
      data[i * 4 + ch] = Math.round(v * 255);
    }
  }
  return data;
}

function _volumeTexture(data: Uint8Array, size: number): THREE.Data3DTexture {
  const tex = new THREE.Data3DTexture(data, size, size, size);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.wrapR = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

/**
 * 3D fractional Brownian motion via iterated 3D value-noise (not true
 * Perlin). `baseFreq` is how many noise cells fit across the volume (so
 * baseFreq=4 means 4×4×4 lattice points for the base octave).
 *
 * 4 octaves, gain 0.5, lacunarity 2 — a standard FBM shape.
 */
function _fbm3D(size: number, baseFreq: number): Float32Array {
  const out = new Float32Array(size * size * size);
  const octaves = 4;
  const gain = 0.5;
  const lacunarity = 2.0;

  for (let z = 0; z < size; z++) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        let sum = 0;
        let amp = 1;
        let freq = baseFreq;
        let norm = 0;
        for (let o = 0; o < octaves; o++) {
          const fx = (x / size) * freq;
          const fy = (y / size) * freq;
          const fz = (z / size) * freq;
          sum += amp * _valueNoise3D(fx, fy, fz, freq);
          norm += amp;
          amp *= gain;
          freq *= lacunarity;
        }
        out[z * size * size + y * size + x] = sum / norm;
      }
    }
  }
  return out;
}

/**
 * 3D value noise on a toroidal lattice of size `period` (so tiling wraps).
 * Uses a deterministic hash for the lattice values; trilinear interpolation
 * between the 8 corners of the containing cube with a smoothstep falloff.
 *
 * Output range approximately [-1, 1] after the centering step.
 */
function _valueNoise3D(x: number, y: number, z: number, period: number): number {
  const p = Math.max(1, Math.floor(period));
  const x0 = ((Math.floor(x) % p) + p) % p;
  const y0 = ((Math.floor(y) % p) + p) % p;
  const z0 = ((Math.floor(z) % p) + p) % p;
  const x1 = (x0 + 1) % p;
  const y1 = (y0 + 1) % p;
  const z1 = (z0 + 1) % p;
  const fx = _smoothstep(x - Math.floor(x));
  const fy = _smoothstep(y - Math.floor(y));
  const fz = _smoothstep(z - Math.floor(z));

  // Trilinear interpolate the 8 corners
  const c000 = _hash3D(x0, y0, z0);
  const c100 = _hash3D(x1, y0, z0);
  const c010 = _hash3D(x0, y1, z0);
  const c110 = _hash3D(x1, y1, z0);
  const c001 = _hash3D(x0, y0, z1);
  const c101 = _hash3D(x1, y0, z1);
  const c011 = _hash3D(x0, y1, z1);
  const c111 = _hash3D(x1, y1, z1);

  const x00 = c000 * (1 - fx) + c100 * fx;
  const x10 = c010 * (1 - fx) + c110 * fx;
  const x01 = c001 * (1 - fx) + c101 * fx;
  const x11 = c011 * (1 - fx) + c111 * fx;
  const y0v = x00 * (1 - fy) + x10 * fy;
  const y1v = x01 * (1 - fy) + x11 * fy;
  const v = y0v * (1 - fz) + y1v * fz;
  // v is in [0, 1]; recenter to [-1, 1]
  return v * 2.0 - 1.0;
}

/** Deterministic hash → [0, 1). Cheap and reproducible. */
function _hash3D(x: number, y: number, z: number): number {
  // FNV-like mix, truncated to a [0,1) float.
  let h = 0x811c9dc5 >>> 0;
  h = Math.imul(h ^ x, 0x01000193) >>> 0;
  h = Math.imul(h ^ y, 0x01000193) >>> 0;
  h = Math.imul(h ^ z, 0x01000193) >>> 0;
  // Stir bits to decorrelate across axes
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function _smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Default weather map — irregular multi-octave FBM blobs producing natural,
 * SCATTERED cloud masses (varied size + spacing, with clear sky between)
 * rather than a uniform field of identical puffs. Users can replace with
 * authored imagery.
 *
 * Two ingredients give the "real cloudscape" look:
 *   1. **Domain warping** — the sample coordinate is displaced by a second
 *      low-freq noise field, so blob edges meander instead of sitting on a
 *      regular grid (this kills the "row of identical puffs" artefact).
 *   2. **Contrast shaping** — the FBM (which clusters around its mean) is
 *      pushed through a smootherstep so most of the map reads as CLEAR
 *      (low coverage) with occasional dense masses. The renderer's coverage
 *      lever then carves clear gaps out of this field; a flat ~0.5 map could
 *      never produce a partly-cloudy sky.
 *
 * The large feature octave (3 cells across the 512² tile) makes individual
 * cloud masses span a big fraction of the visible sky, so they don't look
 * like small repeating tiles when projected onto the altitude slab.
 *
 * Channels:
 *   R — coverage (0..1, contrast-shaped FBM — wide range, clear sky common)
 *   G — cloud-type (≈0.5 — stratocumulus default)
 *   B — wetness (low baseline)
 *   A — anvil (low baseline)
 */
function _bakeDefaultWeatherMap(size: number): Uint8Array {
  const data = new Uint8Array(size * size * 4);
  // Pass 1 — generate the shaped coverage field into floats.
  const cov = new Float32Array(size * size);
  let covMax = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;

      // Domain warp: displace the sample point by a low-freq field so blob
      // boundaries are irregular (no grid-aligned puffs). The warp tiles on
      // the same period so the result still wraps seamlessly.
      const wx = _weatherFbm(u * 2.0, v * 2.0, 2, 2) * 0.18;
      const wy = _weatherFbm(u * 2.0 + 5.2, v * 2.0 + 1.3, 2, 2) * 0.18;

      // Multi-octave FBM with a LARGE base feature (3 cells across the tile)
      // so cloud masses are big + sparse, not a fine repetitive texture.
      const f = _weatherFbm(u * 3.0 + wx, v * 3.0 + wy, 4, 3); // ~[-1, 1]
      let c = f * 0.5 + 0.5;                                 // → [0, 1]

      // Contrast shaping — bias toward CLEAR so the default map is
      // partly-cloudy, not an overcast deck. smootherstep widens the clear
      // valleys while keeping dense peaks; the offset pulls the mean down.
      // (Retuned after the tiling-hash fix changed the field distribution —
      // ×1.25/−0.30 lands the normalized mean near ⅓, masses at 1.)
      c = _smootherstep(Math.max(0, Math.min(1, c * 1.25 - 0.30)));
      cov[y * size + x] = c;
      if (c > covMax) covMax = c;
    }
  }
  // Pass 2 — RANGE-NORMALIZE the coverage channel so the densest masses reach
  // ~1.0. WHY (the "invisible clouds" bug): amplitude-normalized FBM clusters
  // around its mean — the raw shaped field peaked at ~0.72 — while the
  // renderer's coverage mapping (`cov = knob·weather.r·2`) assumes dense
  // masses read ≈1. The mismatch drove the density remap negative almost
  // everywhere and the whole volumetric layer composited at ~0 opacity.
  // Normalizing preserves the clear-bias SHAPE (mean stays low) but restores
  // the intended dynamic range.
  const norm = covMax > 0 ? 1 / covMax : 1;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const idx = (y * size + x) * 4;
      const u = x / size;
      const v = y / size;
      // G — cloud TYPE (Nubis-style): 0 = low flat stratocumulus deck,
      // 1 = tall cumulus congestus tower. An independent low-freq FBM
      // (offset into a different lattice region) so type varies BETWEEN
      // masses, not within one — the renderer compresses each cloud's
      // height profile by this value, which is what makes neighbouring
      // clouds different heights/shapes instead of one repeated bell.
      const t = _weatherFbm(u * 2.0 + 11.7, v * 2.0 + 7.9, 3, 2) * 0.5 + 0.5;
      // B — cluster/size octave: a mid-freq FBM that regionally scales
      // coverage, breaking the sky into fields of many small puffs vs a
      // few big massed decks (perceived size variety).
      const s = _weatherFbm(u * 5.0 + 3.1, v * 5.0 + 23.4, 3, 5) * 0.5 + 0.5;
      data[idx + 0] = Math.round(Math.max(0, Math.min(1, cov[y * size + x] * norm)) * 255);
      data[idx + 1] = Math.round(Math.max(0, Math.min(1, t)) * 255);
      data[idx + 2] = Math.round(Math.max(0, Math.min(1, s)) * 255);
      data[idx + 3] = 0;              // anvil
    }
  }
  return data;
}

function _weatherTexture(data: Uint8Array, size: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, size, size);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.needsUpdate = true;
  return tex;
}

/**
 * 2D FBM for the weather map: `octaves` of value noise on a toroidal lattice
 * (lacunarity 2, gain 0.5), amplitude-normalized to ≈[-1, 1]. The base
 * frequency is the caller's `u`/`v` scale; each octave doubles it.
 */
function _weatherFbm(u: number, v: number, octaves: number, span: number): number {
  let sum = 0, amp = 1, freq = 1, norm = 0;
  for (let o = 0; o < octaves; o++) {
    // Lattice period = the caller's coordinate SPAN × this octave's frequency,
    // so the noise wraps exactly when the caller's [0,1] tile coordinate wraps.
    // WHY: the old fixed period (64) never divided the sampled span, so the
    // 512² weather texture did NOT tile — RepeatWrapping exposed a
    // razor-straight density seam at every integer UV (in-game: a vertical
    // cloud cut at world X=0/Z=0 and every 50 km). Mirrors _fbm3D, which
    // always tiled correctly by matching lattice period to frequency.
    sum += amp * _weatherNoise2D(u * freq, v * freq, Math.max(1, Math.round(span * freq)));
    norm += amp;
    amp *= 0.5;
    freq *= 2.0;
  }
  return sum / norm;
}

function _weatherNoise2D(u: number, v: number, p: number): number {
  const x0 = ((Math.floor(u) % p) + p) % p;
  const y0 = ((Math.floor(v) % p) + p) % p;
  const x1 = (x0 + 1) % p;
  const y1 = (y0 + 1) % p;
  const fx = _smoothstep(u - Math.floor(u));
  const fy = _smoothstep(v - Math.floor(v));
  const c00 = _hash2D(x0, y0);
  const c10 = _hash2D(x1, y0);
  const c01 = _hash2D(x0, y1);
  const c11 = _hash2D(x1, y1);
  const a = c00 * (1 - fx) + c10 * fx;
  const b = c01 * (1 - fx) + c11 * fx;
  return (a * (1 - fy) + b * fy) * 2 - 1;
}

/** Ken Perlin's smootherstep (C2-continuous) — softer cloud-edge falloff. */
function _smootherstep(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function _hash2D(x: number, y: number): number {
  let h = 0x811c9dc5 >>> 0;
  h = Math.imul(h ^ x, 0x01000193) >>> 0;
  h = Math.imul(h ^ y, 0x01000193) >>> 0;
  // Full murmur-style finalizer (same as _hash3D). WHY: FNV's single
  // xor-shift barely avalanches the LAST-mixed coordinate — with the small
  // per-octave tiling lattices (3–24 values per axis) the y contribution
  // almost cancelled and the weather map degenerated into u-axis stripes
  // (the same weak-avalanche family as the terrain FNV lattice-hash fix).
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
